import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { ArrowLeft } from "lucide-react";

// NOTE: This is on-brand TEMPLATE content. Replace the `sections` text below
// (or send your own HTML/text) with your final legal copy.
const DOCS = {
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated: June 2026",
    intro:
      "Nikah Match (\u201cwe\u201d, \u201cus\u201d) is a privacy-first platform for Muslims seeking marriage. This policy explains what we collect, how we use it, and the choices you have.",
    sections: [
      ["Information we collect", "Account details (name, email), profile information you choose to share (age, location, sect, preferences, photos), and usage data needed to operate the service."],
      ["How we use your information", "To create your profile, suggest compatible matches, enable mutual-consent contact sharing, keep the community safe, and improve the service."],
      ["Contact sharing", "Your contact details (email / phone / WhatsApp) are revealed to another member only after a mutual match, and only per the sharing settings you choose."],
      ["Photos & privacy", "Photos may be blurred or watermarked to other members until a mutual match, depending on your privacy settings."],
      ["Data retention & deletion", "You can delete your account at any time from Account & security. Deletion removes your profile across web, Android and iOS."],
      ["Your rights", "You may access, correct, or delete your data. Contact us for any privacy request."],
      ["Contact", "Questions about privacy? Email support@nikah-match.com."],
    ],
  },
  terms: {
    title: "Terms of Service",
    updated: "Last updated: June 2026",
    intro:
      "By creating an account you agree to these terms. Nikah Match is intended for adult Muslims sincerely seeking a spouse in a halal manner.",
    sections: [
      ["Eligibility", "You must be at least 18 years old and legally able to marry. One account per person; accurate information is required."],
      ["Acceptable use", "Be respectful and honest. No harassment, impersonation, solicitation, scams, or non-marriage-related use. Violations may lead to suspension or removal."],
      ["Member content", "You are responsible for your profile accuracy and photos. Do not copy, download, or misuse another member\u2019s information or photos."],
      ["Premium & billing", "Premium unlocks additional features. Subscriptions purchased via Google Play or Apple are managed in the respective app store; web subscriptions are managed via our payment provider."],
      ["No guarantees", "No matchmaking platform can guarantee outcomes or that every member is genuine. Always use caution and verify identity before meeting."],
      ["Termination", "We may suspend or terminate accounts that violate these terms or endanger the community."],
      ["Contact", "Questions about these terms? Email support@nikah-match.com."],
    ],
  },
  community: {
    title: "Community Guidelines",
    updated: "Last updated: June 2026",
    intro:
      "Nikah Match is a serious, privacy-first platform for marriage \u2014 not a dating app. These guidelines keep our community safe and respectful.",
    sections: [
      ["Marriage intention", "Use Nikah Match only with a sincere intention for marriage. Keep conversations respectful and purposeful."],
      ["Respect & modesty", "Treat every member with dignity. No harassment, inappropriate messages, or immodest content."],
      ["Protect yourself", "Never send money or share sensitive documents (home address, banking, passport). Report anyone who pressures you or requests money."],
      ["Verify before meeting", "Use video calls and involve trusted family or a wali before meeting in person. Choose public places for first meetings."],
      ["Honest profiles", "Represent yourself truthfully. Fake or misleading profiles will be removed."],
      ["Reporting", "If you see suspicious or harmful behavior, report the profile immediately \u2014 our team reviews every report."],
      ["Contact", "Need help? Email support@nikah-match.com."],
    ],
  },
};

export default function LegalPage({ doc }) {
  const d = DOCS[doc] || DOCS.privacy;
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="border-b border-emerald-900/10 bg-white/70 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <Link to="/" data-testid="legal-home-link"><Logo size="sm" /></Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm font-semibold text-emerald-800 hover:underline" data-testid="legal-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-12" data-testid={`legal-${doc}`}>
        <h1 className="font-serif text-4xl font-bold text-emerald-950 sm:text-5xl">{d.title}</h1>
        <p className="mt-2 text-sm text-slate-400">{d.updated}</p>
        <p className="mt-6 text-lg leading-relaxed text-slate-600">{d.intro}</p>

        <div className="mt-10 space-y-8">
          {d.sections.map(([heading, body]) => (
            <section key={heading}>
              <h2 className="font-serif text-2xl font-semibold text-emerald-900">{heading}</h2>
              <p className="mt-2 leading-relaxed text-slate-600">{body}</p>
            </section>
          ))}
        </div>

        <div className="mt-14 flex flex-wrap gap-4 border-t border-emerald-900/10 pt-6 text-sm">
          <Link to="/privacy" className="text-emerald-700 hover:underline">Privacy Policy</Link>
          <Link to="/terms" className="text-emerald-700 hover:underline">Terms of Service</Link>
          <Link to="/community" className="text-emerald-700 hover:underline">Community Guidelines</Link>
        </div>
      </main>
    </div>
  );
}
